/**
 * Hand the browser a file the page just built.
 *
 * Same shape as the download helpers in the capture flow: an anchor with `download`, clicked, then
 * revoked. A blob URL is SAME-ORIGIN, which is the whole reason `download` is honoured here — the
 * attribute is ignored for a cross-origin URL, which is why a presigned S3 link has to carry its filename
 * in a Content-Disposition header instead (see `lib/storage/s3.ts`).
 */

export function downloadBlob(bytes: BlobPart, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  // Left long enough for the download to start; revoking immediately cancels it in some browsers.
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 1500);
}

/**
 * Open a document in a new tab and raise the print dialog, where the browser's own "Save as PDF" produces
 * the file. Returns false when the tab was blocked, so the caller can say so rather than appearing to do
 * nothing — a popup blocker is the one failure a user cannot diagnose from an unchanged screen.
 *
 * NO `noopener` on the open, however much it looks like it belongs: passing it makes `window.open` return
 * null by specification, so there would be no handle to write the document into. The window still opens,
 * which produces the worst possible pair of symptoms — a blank pop-up AND a "pop-ups are blocked"
 * message, with no blocker involved. Safe to omit, because the document is our own HTML written into a
 * blank same-origin window; `noopener` exists to stop an untrusted destination reaching back through
 * `window.opener`, and there is no untrusted destination here. The receipt printer learned this first.
 */
export function printDocument(html: string): boolean {
  const w = window.open('', '_blank', 'width=1100,height=900');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  // `focus` before `print`: a background tab's dialog can open behind the window that spawned it.
  w.focus();
  w.onafterprint = () => w.close();
  w.print();
  return true;
}
